import {ImageResourceItem, Inject, ModelEvents, SimpleBaseController, SoundType} from "@denishussar/html5-game-engine";
import {Container, Rectangle, Sprite} from "pixi.js";
import {ModalsNames} from "../../Common/MadalsManager/ModalsList";
import {LocalizationContents} from "../..//LocalizationContents";
import {GameLogic} from '../../Logic/GameLogic';
import {GameModel} from '../../Models/GameModel';
import {ConfirmationsYouRewardType} from "../Modals/Confirmations/ConfirmationsYouRewardController";
import gsap from 'gsap';
import {IConfirmationsConfirmControllerOptions} from "../Modals/Confirmations/ConfirmationsConfirmController";
import {
    GameEvent,
    GameEventPopupButtonName,
    GameEventPopupName, GameEventRewardButtonClick,
    GameEventSceneButtonClick
} from "../../Constants/GameEvent";
import {AdsLogic} from "../../Logic/Ads/AdsLogic";
import {REWARDED_ADS_TYPE} from "../../Logic/Ads/BaseAdsLogic";

enum CoinStateEnum {
    small = 0,
    large,
    check
}

class CoinItem {
    constructor(
        public index: number,
        public state = CoinStateEnum.small,
        public percent = 0,
        public sprite: Container
    ) {
    }
}

export interface IPaletteMonetizationPointsConfigurations {
}

export class PaletteMonetizationPointsController extends SimpleBaseController {
    $model: GameModel;
    private bounds = new Rectangle();
    private coins: CoinItem[] = [];
    private lastCoin: CoinItem;

    public constructor(
        @Inject private options: any,
        @Inject private gameLogic: GameLogic,
        @Inject private modelEvents: ModelEvents,
        @Inject private model: GameModel,
        @Inject private adsLogic: AdsLogic,
    ) {
        super('PaletteMonetizationPointsController');
    }

    private LOCALSTORAGE_NAME = 'PaletteMonetizationPointsController';

    setBounds(x: number, y: number, width: number, height: number) {
        this.bounds.x = x;
        this.bounds.y = y;
        this.bounds.width = width;
        this.bounds.height = height;

        this.updatePercentBarPositionByProgress();
    }

    protected getConfiguration(): IPaletteMonetizationPointsConfigurations {
        const resources = {
            'palette_check': new ImageResourceItem('palette_check', 'palette_check.png'),
            'palette_coin_large': new ImageResourceItem('palette_coin_large', 'palette_coin_large.png'),
            'palette_coin_small': new ImageResourceItem('palette_coin_small', 'palette_coin_small.png'),
        };
        return {
            resources,
            modelEvents: {
                progressColor: (color: number, index: number, progress: number) => {
                    this.updateProgress();
                },
            }
        };
    }

    protected onInitialize(): void {
        const size = this.gameLogic.getPictureSize();
        let count = 4;
        if (size.width < 50) {
            count = 1;
        } else if (size.width === 50) {
            count = 2;
        } else if (size.width < 70) {
            count = 3;
        }
        const deltaPercent = (100 / (count + 1)) / 100;
        const coinTexture = this.resourcesLoader.loader.resources['palette_coin_large'].texture ?? null;
        for (let i = 0; i < count; i++) {
            const index = i;
            const coin = new Sprite(coinTexture);
            coin.interactive = false;
            coin.anchor.set(0.5);
            coin.scale.set(.7);
            this.display.addChild(coin);

            this.coins.push(new CoinItem(index, CoinStateEnum.small, (i + 1) * deltaPercent, coin));
        }

        const s1 = new Sprite(coinTexture)
        s1.anchor.set(0.5);
        s1.x = -s1.width / 2;
        s1.y = 0;

        const s2 = new Sprite(coinTexture)
        s2.anchor.set(0.5);
        s2.x = s2.width * .3;
        s2.y = -s2.width * .3;

        const s3 = new Sprite(coinTexture)
        s3.anchor.set(0.5);
        s3.x = s3.width * .3;
        s3.y = s3.width * .3;

        const container = new Container();
        container.addChild(s1);
        container.addChild(s2);
        container.addChild(s3);
        this.display.addChild(container);

        this.lastCoin = new CoinItem(this.coins.length, CoinStateEnum.small, 1, container);

        this.updateProgress();
    }

    protected updatePercentBarPositionByProgress(): void {
        const deltaPercent = (100 / (this.coins.length + 1)) / 100;
        if (this.horizontal) {
            const deltaPixel = this.bounds.width * deltaPercent;

            for (let i = 0; i < this.coins.length; i++) {
                this.coins[i].sprite.y = this.bounds.y;
                this.coins[i].sprite.x = (i + 1) * deltaPixel;
            }

            this.lastCoin.sprite.y = this.bounds.y;
            this.lastCoin.sprite.x = this.bounds.width - this.lastCoin.sprite.width / 2;
        } else {
            const deltaPixel = this.bounds.height * deltaPercent;

            for (let i = 0; i < this.coins.length; i++) {
                this.coins[i].sprite.x = this.bounds.x;
                this.coins[i].sprite.y = (i + 1) * deltaPixel;
            }

            this.lastCoin.sprite.x = this.bounds.x;
            this.lastCoin.sprite.y = this.bounds.height - this.lastCoin.sprite.height / 2;
        }
    }

    protected updateProgress() {
        const progress = this.gameLogic.getFillProgress();
        for (let i = 0; i < this.coins.length; i++) {
            const item = this.coins[i];
            if (progress < item.percent) {
                continue;
            }

            if (item.state !== CoinStateEnum.small) {
                continue;
            }
            const tookAward = this.getLocalStorageValue(i);
            if (tookAward === '1') {
                this.setStateCheck(item);
                continue;
            }
            this.setStateLargeCoin(item);
            this.setLocalStorageValue(i, false);
        }
    }

    protected handlePointerClick = (item: CoinItem) => {
        if (item.state !== CoinStateEnum.large) {
            return;
        }

        const reward = 10;

        this.gameLogic.modalsManager.show(ModalsNames.buyEmbroidery, {
            type: ConfirmationsYouRewardType.money,
            text: LocalizationContents.getText('Your Reward'),
            valueText: 'x' + reward,
            buyText: LocalizationContents.getText('Thanks'),
            receiveText: 'x2 ' + LocalizationContents.getText('REWARD'),
            buyClick: () => {
                this.modelEvents.dispatch(this.model, GameEvent.popupButtonClick, { buttonName: GameEventPopupButtonName.collect });

                this.setLocalStorageValue(item.index, true);
                this.setStateCheck(item);

                this.model.rewardsCount++;
                this.modelEvents.dispatch(this.model, GameEvent.rewardButtonClick, <GameEventRewardButtonClick>{
                    buttonName: GameEventPopupButtonName.collect,
                    goal: this.model.rewardsCount,
                    reactionTime: 0,
                    status: true,
                });

                this.gameLogic.addBalanceCoins(reward);
                this.gameLogic.modalsManager.hide();
            },
            exitClick: () => {
                this.gameLogic.modalsManager.hide();
            },
            receiveClick: () => {
                this.modelEvents.dispatch(this.model, GameEvent.popupButtonClick, { buttonName: GameEventPopupButtonName.collect_x2 });

                if (this.adsLogic.isReady && this.adsLogic.adsLogic.hasAdBlock()) {
                    this.gameLogic.modalsManager.hide();
                    this.gameLogic.modalsManager.show(ModalsNames.confirm, <IConfirmationsConfirmControllerOptions>{
                        oneButtonMode: true,
                        text: LocalizationContents.getText('Adblock'),
                        yesText: LocalizationContents.getText('Ok'),
                        yesClick: () => this.gameLogic.modalsManager.hide(),
                    });
                } else {
                    this.showAdvertisment()
                        .then(() => {
                        })
                        .catch(() => {
                            console.log('something wrong with ads');
                        })
                        .finally(async () => {
                            await this.gameLogic.modalsManager.hide();

                            this.setLocalStorageValue(item.index, true);
                            this.setStateCheck(item);

                            const onClose = async () => {
                                this.model.rewardsCount++;
                                this.modelEvents.dispatch(this.model, GameEvent.rewardButtonClick, <GameEventRewardButtonClick>{
                                    buttonName: GameEventPopupButtonName.collect_x2,
                                    goal: this.model.rewardsCount,
                                    reactionTime: 0,
                                    status: true,
                                });

                                this.gameLogic.addBalanceCoins(reward * 2);
                                await this.gameLogic.modalsManager.hide();
                            };
                            this.gameLogic.modalsManager.show(ModalsNames.youReward, {
                                type: ConfirmationsYouRewardType.money,
                                value: `${reward * 2}`,
                                text: LocalizationContents.getText('Your Reward'),
                                okText: LocalizationContents.getText('Thanks'),
                                skipAnalytics: true,
                                okClick: () => onClose(),
                                exitClick: () => onClose(),
                            });
                        });
                }
            },
        });
    }

    private async showAdvertisment(): Promise<void> {
        if (!this.adsLogic.isReady) {
            return Promise.resolve();
        }

        this.soundManager.muteSoundsByType(true, SoundType.ALL);

        return this.adsLogic.adsLogic.requestAd()
            .finally(() => {
                this.soundManager.muteSoundsByType(false, SoundType.ALL);
            });
    }

    private get horizontal() {
        return !(this.bounds.width < this.bounds.height);
    }

    private setLocalStorageValue(index: number, tookTheAward: boolean) {
        if (tookTheAward) {
            localStorage.setItem(`${this.LOCALSTORAGE_NAME}_${this.model.picture.id}_${index}`, '1');
        } else {
            localStorage.setItem(`${this.LOCALSTORAGE_NAME}_${this.model.picture.id}_${index}`, '0');
        }
    }

    private getLocalStorageValue(index: number) {
        return localStorage.getItem(`${this.LOCALSTORAGE_NAME}_${this.model.picture.id}_${index}`);
    }

    private setStateLargeCoin(item: CoinItem) {
        if (item.state === CoinStateEnum.large) {
            return;
        }

        item.state = CoinStateEnum.large;
        item.sprite.interactive = true;
        item.sprite.scale.set(1);

        item.sprite.buttonMode = true;
        item.sprite.interactive = true;
        item.sprite.on('pointerdown', () => {
            this.handlePointerClick(item);
        });

        try {
            gsap.to(item.sprite, .3, {
                alpha: 0, yoyo: true, repeat: 10, ease: 'circ.in', onComplete: () => {
                    item.sprite.alpha = 1;
                }
            });
        } catch (err) {
        }

        const timeout = 6000;
        setTimeout(function animate() {
            try {
                if (item.state === CoinStateEnum.large) {
                    gsap.to(item.sprite, .3, {
                        alpha: 0, yoyo: true, repeat: 10, ease: 'circ.in', onComplete: () => {
                            item.sprite.alpha = 1;
                        }
                    });

                    setTimeout(() => {
                        animate();
                    }, timeout);
                }
            } catch (err) {
            }
        }, timeout);
    }

    private setStateCheck(item: CoinItem) {
        if (item.state === CoinStateEnum.check) {
            return;
        }

        item.sprite.interactive = false;

        item.state = CoinStateEnum.check;

        const coinTexture = this.resourcesLoader.loader.resources['palette_check'].texture ?? null;

        const coin = new Sprite(coinTexture);
        coin.x = item.sprite.x;
        coin.y = item.sprite.y;
        coin.interactive = false;
        coin.anchor.set(0.5);
        this.display.addChild(coin);

        item.sprite.destroy();
        item.sprite = coin;
    }
}
